// Pure helpers for the Device Specification Utility (Index Sheet Builder, Device Spec, Panel Spec).
// The server (DsSpecController / DsSpecRules) owns every rule that decides a write; these only shape
// what the page shows before it asks.
import { buildIndexSheetCode, parseIndexSheetCode, classifyIndexSheetCode, KIND, parseSizeInches } from './indexSheetCode';

export const STATUSES = ['Preferred', 'Active', 'Do Not Use', 'Obsolete'];
export const TYPES = ['Tag', 'Panel'];
export const SUFFIXES = ['', 'FL', 'FO', 'FC'];

// The Builder shows every size as xx.xx" so the list reads (and sorts) the same whatever the
// database stored: 3/8" -> 00.38", 1.25" -> 01.25". Text that is not a size (N/A) is shown as is.
export function sizeLabel(size) {
  const r = parseSizeInches(size);
  if (!r) return String(size ?? '');
  const [whole, frac] = (r.num / r.den).toFixed(2).split('.');
  return `${whole.padStart(2, '0')}.${frac}"`;
}

// Smallest first; anything that is not a size goes last, in its original order.
export function sortSizes(sizes) {
  const val = z => { const r = parseSizeInches(z.size); return r ? r.num / r.den : Infinity; };
  return [...(sizes ?? [])].sort((a, b) => val(a) - val(b));
}
export const PANEL_PREFIXES = ['CPL', 'SPL', 'PPL', 'LCP', 'VCP'];

export const defaultType = isa => (PANEL_PREFIXES.includes(String(isa ?? '').trim().toUpperCase()) ? 'Panel' : 'Tag');

// A group typed by hand: 1-3 digits, padded to three. Null when it is not one.
export function normalizeGroup(g) {
  const t = String(g ?? '').trim();
  return /^[0-9]{1,3}$/.test(t) ? t.padStart(3, '0') : null;
}

// Size when any chosen tag carries a size that gives a group, otherwise Series.
export const defaultBasis = tags => ((tags ?? []).some(t => t?.group) ? 'Size' : 'Series');

// The guided builder's group: the picked size's group, or the hand-entered one when the size gives
// none or the basis is Series.
export function guidedGroup({ basis, sizeGroup, manualGroup }) {
  if (basis === 'Size' && sizeGroup) return sizeGroup;
  return normalizeGroup(manualGroup);
}

// ISA-MFR-GRP, or null while a part is missing or invalid.
export function guidedPrefix({ isa, mfrCode, group }) {
  if (!isa || !mfrCode || !group) return null;
  const p = parseIndexSheetCode(`${isa}-${mfrCode}-${group}-001`);
  return p ? p.prefix : null;
}

// { code, error }: the code the guided builder would create, or why it cannot yet.
export function guidedCode({ isa, mfrCode, group, instance, suffix }) {
  if (!isa) return { code: null, error: 'Pick an ISA type.' };
  if (!mfrCode) return { code: null, error: 'Pick a manufacturer.' };
  if (!group) return { code: null, error: 'Enter the group (1-3 digits).' };
  if (!instance) return { code: null, error: 'Finding the next free instance…' };
  try {
    return { code: buildIndexSheetCode(isa, mfrCode, group, instance, suffix), error: null };
  } catch (e) {
    return { code: null, error: e.message };
  }
}

// Live check of a typed code: { ok, message, parts }. Uniqueness is the server's to judge.
export function manualCheck(code) {
  const kind = classifyIndexSheetCode(code);
  if (kind === KIND.PLACEHOLDER) {
    return { ok: false, message: String(code ?? '').trim() ? 'That is a placeholder, not an Index Sheet code.' : 'Type the full code.', parts: null };
  }
  if (kind === KIND.NONCONFORMING) {
    return { ok: false, message: 'Does not follow <ISA>-<MFR>-<###>-<###>[-FL|FO|FC].', parts: null };
  }
  return { ok: true, message: null, parts: parseIndexSheetCode(code) };
}

// This database's manufacturers carrying a code, matched case-insensitively.
export const manufacturersWithCode = (manufacturers, code) =>
  (manufacturers ?? []).filter(m => String(m.indexCode ?? '').toUpperCase() === String(code ?? '').trim().toUpperCase());

export const manufacturerLabel = m => `${m.indexCode} — ${m.manufacturer ?? ''}${m.sharedCode ? ' (code shared)' : ''}`;

// What "Open in Builder" fills in from a catalog sheet: ISA type and manufacturer matched by code in
// this database, the sheet's group as a series group, its suffix. `missing` names what this database
// lacks, so the page can say so.
export function prefillFromCatalog(row, isaTypes, manufacturers) {
  const parts = parseIndexSheetCode(row?.indexSheet);
  if (!parts) return null;
  const isa = (isaTypes ?? []).find(t => String(t.isaType ?? '').toUpperCase() === parts.isa);
  const mfrs = manufacturersWithCode(manufacturers, parts.mfr);
  const missing = [];
  if (!isa) missing.push(`ISA type ${parts.isa}`);
  if (mfrs.length === 0) missing.push(`a manufacturer with code ${parts.mfr}`);
  return {
    isaTypeId: isa?.id ?? '',
    manufacturerId: mfrs[0]?.id ?? '',
    basis: 'Series',
    manualGroup: parts.group,
    suffix: parts.suffix,
    description: row.description ?? '',
    model: row.model ?? '',
    specification: row.specification ?? '',
    type: row.type && TYPES.includes(row.type) ? row.type : defaultType(parts.isa),
    source: row.indexSheet,
    origin: row.source ?? 'Catalog',
    missing,
  };
}

// The default description of a proposed sheet: its sizes, then the ISA type's description.
export const proposalDescription = (proposal, isaDesc) =>
  [(proposal?.sizes ?? []).join(', '), isaDesc].filter(Boolean).join(' ').slice(0, 220);

// One line for the result of a create: what was made, and what was refused with why.
export function createSummary(data) {
  const results = data?.results ?? [];
  const ok = results.filter(r => r.status === 201);
  const bad = results.filter(r => r.status !== 201);
  const tags = ok.reduce((n, r) => n + (r.tagsAssigned ?? 0), 0);
  return {
    success: ok.length
      ? `Created ${ok.map(r => r.code).join(', ')}` + (tags ? `; ${tags} tag${tags === 1 ? '' : 's'} assigned.` : '.')
      : null,
    failure: bad.length ? bad.map(r => `${r.code || '(no code)'}: ${r.error}`).join(' ') : null,
  };
}

// The text to show for a failed call: the server's own message when it sent one, the
// access-denied text on a 403, otherwise the fallback.
export function errText(e, fallback) {
  if (e?.response?.status === 403) return 'Access denied. You do not have permission to use the Device Specification Utility.';
  return e?.response?.data?.error || fallback;
}
